import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { LanguageService } from "../../shared/services";
import { environment } from "../../../environments/environment";
import { isPlatformBrowser, Location } from "@angular/common";

interface HomePageURI {
  url: string;
  queryParams: string;
}

/**
 * Single source of truth for each deployment's default (suffixless) language.
 * Keyed by `environment.platformId`. The suffix `/lang/<code>` is emitted only
 * when the active language differs from this default. NOTE: this registry holds
 * ONLY the default language — never domains (domains are environment-specific and
 * always come from `environment.*`).
 */
const PLATFORM_DEFAULT_LANG: { [platformId: string]: "pt" | "en" | "es" } = {
  br: "pt",
  mx: "es",
  co: "es",
  latam: "en"
};

/**
 * Centralized language -> URL suffix code map. The single place encode/decode of
 * the `/lang/<code>` segment is defined, so it can never drift from the parsing
 * in `getLanguageBasedOnURL`/`removeLanguageFromURL`.
 */
const LANG_SUFFIX_CODE: { [lang: string]: string } = {
  en: "en",
  pt: "pt-br",
  es: "es"
};

/**
 * Language -> URL slug PREFIX for the news-detail route. This is the ONLY route
 * whose slug word changes per language (`/news/...` in English, `/noticias/...`
 * in Portuguese & Spanish). Single source of truth shared by
 * `news.navigate.service` (link building) and the hreflang builder, so the two
 * can never advertise different URLs.
 */
const NEWS_SLUG_BY_LANG: { [lang: string]: string } = {
  en: "news",
  pt: "noticias",
  es: "noticias"
};

/**
 * Every leading slug that resolves to the news-detail component. Includes the
 * legacy accented `notícias`. The `insights/detail` and `insight-preview/detail`
 * shapes are matched separately in `isNewsDetailPath`.
 */
const NEWS_SLUG_ALIASES = ["news", "noticias", "notícias"];

/**
 * `/lang/<code>` URL segment -> internal language. Inverse of `LANG_SUFFIX_CODE`
 * PLUS the legacy bare `pt` code the news link builder emitted before `pt-br`
 * became the canonical suffix (still live in indexed URLs).
 */
const LANG_BY_SUFFIX_CODE: { [code: string]: string } = {
  "pt-br": "pt",
  pt: "pt",
  en: "en",
  es: "es"
};

/**
 * A news-detail URL broken into its parts. `prefix` is normalized (lowercased,
 * accents resolved to `noticias`) for matching, while `slug` and `id` keep the
 * raw segments so a path can be rebuilt without re-encoding surprises.
 */
export interface NewsDetailPathParts {
  prefix: string;
  slug: string;
  id: string;
  langCode: string | null;
}

@Injectable()
export class SiilaTranslationService {
  private currentLanguage: string;
  private supportedLanguages: string[] = ["pt", "en", "es"];
  private currentCode: string;
  // Per-page `{ internalLang -> localized address-bar path }` map for the
  // CURRENT route, populated by `MetaTagService.addHreflangTags` (which already
  // computes these URLs for hreflang) and consumed by the language switcher to
  // re-slug the URL to the new language INSTANTLY, before the reload — no
  // network. Global: any page that emits hreflang gets it for free. Cleared on
  // every navigation so a route with no translated slug falls back to the
  // plain suffix behavior. See `setLocalizedPathMap` / `applyLocalizedPathForLang`.
  private localizedPathMap: { [lang: string]: string } | null = null;

  constructor(
    private _ts: TranslateService,
    private languageService: LanguageService,
    private location: Location,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.currentCode = environment.platformId;
    if (this.currentCode == "mx" || this.currentCode == "co") {
      this.supportedLanguages = ["en", "es"];
    } else if (this.currentCode == "br") {
      this.supportedLanguages = ["pt", "en"];
    } else if (this.currentCode == "latam") {
      this.supportedLanguages = ["en", "es"];
    } else {
      this.supportedLanguages = ["en"];
    }
  }

  /**
   * Returns the default (suffixless) language for a deployment. Defaults to the
   * current deployment (`environment.platformId`). This is the single source of
   * truth shared by the URL builders and `app-initializer-factory.ts`.
   */
  getDefaultLanguage(
    platformId: string = environment.platformId
  ): "pt" | "en" | "es" {
    return PLATFORM_DEFAULT_LANG[platformId] || "en";
  }

  /**
   * The `/lang/<code>` URL segment for THIS deployment's default language
   * (`pt -> pt-br`, `es -> es`, `en -> en`). Single source of truth for callers
   * that need to strip ONLY the default-language suffix (e.g. canonical tags),
   * so the map never drifts from `applyLangSuffix`/`getLanguageBasedOnURL`.
   */
  getDefaultLangSuffixCode(
    platformId: string = environment.platformId
  ): string {
    return LANG_SUFFIX_CODE[this.getDefaultLanguage(platformId)];
  }

  /**
   * Public, read-only view of THIS deployment's supported languages. Returned as
   * a copy so callers (e.g. hreflang builders) cannot mutate the internal set.
   */
  getSupportedLanguages(): string[] {
    return [...this.supportedLanguages];
  }

  /**
   * Low-level suffix engine: appends `/lang/<code>` to `basePathOrUrl` ONLY when
   * `lang` differs from `defaultLang`. Normalizes `en-mx -> en` before deciding,
   * and strips a trailing slash so the suffix attaches cleanly. Pure (no
   * `window`/`history`) so it runs identically in browser and SSR.
   */
  applyLangSuffix(
    basePathOrUrl: string,
    lang: string,
    defaultLang: string
  ): string {
    let path = basePathOrUrl;
    if (path == null || path === undefined) {
      return path;
    }
    const normalizedLang = lang === "en-mx" ? "en" : lang;
    if (path.endsWith("/")) {
      path = path.slice(0, path.length - 1);
    }
    if (normalizedLang === defaultLang) {
      return path;
    }
    const code = LANG_SUFFIX_CODE[normalizedLang];
    return code ? path.concat("/lang/", code) : path;
  }

  getTranslation(textValue: string) {
    this.currentLanguage = this.getCurrentLanguage();
    return this.translateValue(textValue, this.currentLanguage);
  }

  /**
   * Resolve a multilingual JSON string to a SPECIFIC language (not necessarily
   * the active one), applying the same pt->pt-br->en / es->en / first-key
   * fallbacks as `getTranslation`. Kept pure so callers such as
   * `buildNewsDetailPath` can render a slug in any language regardless of the
   * language currently shown on screen.
   */
  private translateValue(textValue: string, language: string) {
    if (textValue) {
      try {
        let newValue = JSON.parse(textValue)[language];

        if (newValue == undefined && language === "pt") {
          newValue = JSON.parse(textValue)["pt-br"]
            ? JSON.parse(textValue)["pt-br"]
            : JSON.parse(textValue)["en"];
        }

        if (newValue == undefined && language === "es") {
          newValue = JSON.parse(textValue)["es"]
            ? JSON.parse(textValue)["es"]
            : JSON.parse(textValue)["en"];
        }

        if (typeof newValue === "object") {
          newValue = newValue[language];
          if (newValue == undefined && language === "pt") {
            newValue = newValue["pt-br"] ? newValue["pt-br"] : newValue["en"];
          }
          if (newValue == undefined && language === "es") {
            newValue = newValue["es"] ? newValue["es"] : newValue["en"];
          }
        }

        let result = newValue
          ? newValue
          : JSON.parse(textValue)[Object.keys(JSON.parse(textValue))[0]];

        return result;
      } catch (e) {
        console.error("error parsing --> ", textValue);
      }
    }
    return textValue;
  }

  /**
   * Normalize a URL path segment: lowercase, spaces/`%`/`?` -> `-`, and strip
   * quotes/brackets/ampersands/slashes. Mirrors the historical inline logic in
   * `news.navigate.service` so linked URLs and advertised hreflang URLs match
   * byte-for-byte.
   */
  slugify(text: string): string {
    return (text || "")
      .toLowerCase()
      .replaceAll(/[ %?]/g, "-")
      .replaceAll(/['"()<>&\\\/]/g, "");
  }

  /** The news-detail slug prefix for a language (`news` in EN, `noticias` in PT/ES). */
  getNewsSlugPrefix(lang: string): string {
    const normalized = lang === "en-mx" ? "en" : lang;
    return NEWS_SLUG_BY_LANG[normalized] || "news";
  }

  /**
   * Build the (suffixless) news-detail base path for a SPECIFIC language:
   * `<prefix(lang)>/<slugified headline-in-lang>/<id>`. Pure string op (no
   * `window`), so it is SSR-safe and can render any language's URL regardless of
   * the language currently active on screen.
   */
  buildNewsDetailPath(
    headlineJson: string,
    id: string | number,
    lang: string
  ): string {
    const normalized = lang === "en-mx" ? "en" : lang;
    const prefix = this.getNewsSlugPrefix(normalized);
    const titleSlug = this.slugify(
      this.translateValue(headlineJson, normalized)
    );
    return `${prefix}/${titleSlug}/${id}`;
  }

  /**
   * True when `path` targets the news-detail component (`DetailNewsComponent`):
   * any of the `news`/`noticias`/`notícias` slug prefixes, or the
   * `insights/detail` / `insight-preview/detail` shapes. Used to let the
   * component own hreflang for these routes instead of the generic builder.
   */
  isNewsDetailPath(path: string): boolean {
    if (!path) {
      return false;
    }
    const clean = path.split("?")[0].replace(/^\/+/, "");
    // `news/detail/:id` is a DIFFERENT component (NewsDetailComponent) that does
    // NOT manage its own hreflang, so leave it to the generic builder as before.
    if (clean.startsWith("news/detail/")) {
      return false;
    }
    const firstSegment = clean.split("/")[0];
    return (
      NEWS_SLUG_ALIASES.includes(firstSegment) ||
      clean.startsWith("insights/detail/") ||
      clean.startsWith("insight-preview/detail/")
    );
  }

  /**
   * Split a news-detail path into `<prefix>/<slug>/<id>[/lang/<code>]`, or return
   * null when it is not that shape. The id MUST be numeric — that is what makes
   * the parse safe to act on, since every canonical URL is rebuilt from the id
   * alone. Accepts a path with or without a leading slash, query string, or
   * percent-encoding (`not%C3%ADcias`).
   */
  parseNewsDetailPath(path: string): NewsDetailPathParts | null {
    if (!path) {
      return null;
    }
    const segments = path
      .split("?")[0]
      .split("#")[0]
      .replace(/^\/+/, "")
      .replace(/\/+$/, "")
      .split("/");
    if (segments.length < 3) {
      return null;
    }

    let prefix: string;
    try {
      prefix = decodeURIComponent(segments[0]).toLowerCase();
    } catch (e) {
      prefix = segments[0].toLowerCase();
    }
    if (!NEWS_SLUG_ALIASES.includes(prefix)) {
      return null;
    }
    // Collapse the legacy accented alias so callers only ever see `noticias`.
    if (prefix === "notícias") {
      prefix = "noticias";
    }

    let langCode: string | null = null;
    let rest = segments;
    if (rest.length >= 5 && rest[rest.length - 2].toLowerCase() === "lang") {
      langCode = rest[rest.length - 1].toLowerCase();
      rest = rest.slice(0, rest.length - 2);
    }
    if (rest.length !== 3 || !/^\d+$/.test(rest[2])) {
      return null;
    }

    return { prefix: prefix, slug: rest[1], id: rest[2], langCode: langCode };
  }

  /**
   * The language a news-detail path's SLUG WORD asks for: `/news/...` is English,
   * `/noticias/...` (and the accented legacy form) is this deployment's non-English
   * language — `pt` on BR, `es` on MX/CO/LATAM. Derived by inverting
   * `getNewsSlugPrefix` over the supported set, so the two can never disagree.
   * Returns undefined for any path that is not a news-detail URL.
   */
  getNewsSlugLanguage(path: string): string | undefined {
    const parts = this.parseNewsDetailPath(path);
    if (!parts) {
      return undefined;
    }
    return this.supportedLanguages.find(
      lang => this.getNewsSlugPrefix(lang) === parts.prefix
    );
  }

  /**
   * The language a URL asks for. On news-detail routes the SLUG WORD wins — the
   * word IS the language, so `/noticias/<slug>/<id>/lang/en` means Spanish (or
   * Portuguese on BR), not English. Every other route falls back to the
   * `/lang/<code>` suffix. Returns undefined when the URL says nothing, or names
   * a language this deployment does not support.
   */
  getLanguageFromPath(path: string): string | undefined {
    const fromSlug = this.getNewsSlugLanguage(path);
    if (fromSlug) {
      return fromSlug;
    }
    return this.getLanguageFromSuffix(path);
  }

  /**
   * Strict `/lang/<code>` reader: the code only counts when it is preceded by the
   * literal `lang` segment, so a path merely ENDING in `en`/`es`/`pt` is not
   * mistaken for a language. Unlike `getLanguageBasedOnURL` it also accepts the
   * legacy bare `pt` code and filters to languages this deployment supports.
   */
  private getLanguageFromSuffix(path: string): string | undefined {
    if (!path) {
      return undefined;
    }
    const segments = path
      .split("?")[0]
      .split("#")[0]
      .replace(/\/+$/, "")
      .split("/");
    if (
      segments.length < 2 ||
      segments[segments.length - 2].toLowerCase() !== "lang"
    ) {
      return undefined;
    }
    const lang =
      LANG_BY_SUFFIX_CODE[segments[segments.length - 1].toLowerCase()];
    return lang && this.supportedLanguages.includes(lang) ? lang : undefined;
  }

  /**
   * Re-slug the leading word of a news-detail path to `lang`, leaving the title
   * slug and id untouched (only the article data can re-slug the title — the
   * canonicalizer in `DetailNewsComponent` does that on arrival).
   *
   * MUST run on every localized-path build. Under the "slug word decides the
   * language" rule a path like `/noticias/<slug>/<id>/lang/en` is a
   * CONTRADICTION, and the canonicalizer would redirect it straight back to
   * Spanish — i.e. the language switcher would bounce the visitor out of the
   * language they just picked. Swapping the word here keeps the two in agreement.
   */
  applyNewsSlugPrefix(path: string, lang: string): string {
    const parts = this.parseNewsDetailPath(path);
    if (!parts) {
      return path;
    }
    const prefix = this.getNewsSlugPrefix(lang);
    if (prefix === parts.prefix) {
      return path;
    }
    const lead = path.startsWith("/") ? "/" : "";
    const suffix = parts.langCode ? `/lang/${parts.langCode}` : "";
    return `${lead}${prefix}/${parts.slug}/${parts.id}${suffix}`;
  }

  /**
   * Whether `textValue` (a multilingual JSON blob such as `headline` or
   * `urlTitle`) actually CARRIES a translation for `lang`. `translateValue`
   * silently falls back (pt -> pt-br -> en, es -> en, then the first key), which
   * is right for display but wrong for deciding whether a language version
   * exists — and that decision picks which URL we redirect to.
   */
  hasTranslation(textValue: string, lang: string): boolean {
    if (!textValue) {
      return false;
    }
    const normalized = lang === "en-mx" ? "en" : lang;
    try {
      const parsed = JSON.parse(textValue);
      const value =
        normalized === "pt" && !parsed["pt"]
          ? parsed["pt-br"]
          : parsed[normalized];
      return !!(value && String(value).trim().length);
    } catch (e) {
      return false;
    }
  }

  /**
   * URL-slug transform matching the `langSelector` pipe's URL mode: spaces ->
   * dashes, lowercased, with NO character stripping (keeps `&`, commas, accents).
   * This is intentionally DIFFERENT from `slugify` (news) — it must stay so, to
   * match the existing solution/product link convention byte-for-byte.
   */
  toUrlSlug(text: string): string {
    return (text || "")
      .split(" ")
      .join("-")
      .toLowerCase();
  }

  /**
   * Build the (suffixless) solution-detail base path for a SPECIFIC language:
   * `solutions/<id>/<langSelector-slug of the name-in-lang>`. The route ignores
   * the name segment (only `:id` drives content) but it must be language-correct
   * for hreflang. Pure string op -> SSR-safe.
   */
  buildSolutionDetailPath(
    nameJson: string,
    id: string | number,
    lang: string
  ): string {
    const normalized = lang === "en-mx" ? "en" : lang;
    return `solutions/${id}/${this.toUrlSlug(
      this.translateValue(nameJson, normalized)
    )}`;
  }

  /** Product twin of `buildSolutionDetailPath` (`products/<id>/<slug>`). */
  buildProductDetailPath(
    nameJson: string,
    id: string | number,
    lang: string
  ): string {
    const normalized = lang === "en-mx" ? "en" : lang;
    return `products/${id}/${this.toUrlSlug(
      this.translateValue(nameJson, normalized)
    )}`;
  }

  /**
   * Full, ready-to-use solution-detail URL: the `:name` slug re-derived to
   * `lang` PLUS the platform `/lang/<code>` suffix and a leading slash — suitable
   * for `updateUrlWithoutNavigation` or an `href`. Single source of truth for
   * solution-detail links, composing `buildSolutionDetailPath` (slug) with
   * `buildLocalizedPath` (platform-default-aware suffix). `lang` defaults to the
   * active language and may be `en-mx` (normalized to `en` internally).
   */
  buildSolutionDetailUrl(
    nameJson: string,
    id: string | number,
    lang: string = this.getCurrentLanguage()
  ): string {
    return this.buildLocalizedPath(
      "/" + this.buildSolutionDetailPath(nameJson, id, lang),
      lang
    );
  }

  /** Product twin of `buildSolutionDetailUrl` (`/products/<id>/<slug>[/lang/<code>]`). */
  buildProductDetailUrl(
    nameJson: string,
    id: string | number,
    lang: string = this.getCurrentLanguage()
  ): string {
    return this.buildLocalizedPath(
      "/" + this.buildProductDetailPath(nameJson, id, lang),
      lang
    );
  }

  /** `solutions/:id/:name` detail route (numeric id) — owned by SolutionComponent. */
  isSolutionDetailPath(path: string): boolean {
    return this.isNumericDetailPath(path, "solutions");
  }

  /** `products/:id/:name` detail route (numeric id) — owned by GrocsComponent. */
  isProductDetailPath(path: string): boolean {
    return this.isNumericDetailPath(path, "products");
  }

  private isNumericDetailPath(path: string, prefix: string): boolean {
    if (!path) {
      return false;
    }
    const seg = path
      .split("?")[0]
      .replace(/^\/+/, "")
      .split("/");
    return seg[0] === prefix && /^\d+$/.test(seg[1] || "");
  }

  /**
   * True when the route's own component manages its hreflang tags (news, solution
   * or product detail). The generic builder in `app.component` must NOT emit
   * same-path tags for these, since only the component knows the per-language
   * translated slug.
   */
  componentOwnsHreflang(path: string): boolean {
    return (
      this.isNewsDetailPath(path) ||
      this.isSolutionDetailPath(path) ||
      this.isProductDetailPath(path)
    );
  }

  getImgSEOTranslation(translationKey: string): string {
    let key = "imgSEO." + translationKey;
    return this.getTranslationFromJSONFiles(key);
  }

  getTranslationFromJSONFiles(translationKey: string): string {
    let result = "";
    this._ts.get(translationKey).subscribe(data => {
      result = data;
    });
    return result;
  }

  getCurrentLanguage() {
    let language: string = "en";
    if (this.currentLanguage === undefined) {
      let storageValue = null;
      try {
        storageValue = localStorage.getItem("currentLanguageHP");
      } catch (error) {}

      if (storageValue) {
        language = storageValue;
      } else {
        language = this._ts.getBrowserLang();
      }
      return this.supportedLanguages.includes(language) ? language : "en";
    }

    return this.currentLanguage;
  }

  setLanguage(language: string, skipLangUrlPath?: boolean) {
    this.registrateLanguage(language);
    sessionStorage.setItem("language", language);
    this.currentLanguage = language;
    if (!skipLangUrlPath)
      this.addCurrentLanguageToPath(window.location.pathname);
    try {
      localStorage.setItem("currentLanguageHP", language);
      // Internal language code stays "pt" (hundreds of `this.lang == "pt"` comparisons
      // rely on it) — only the <html lang> attribute needs the full BCP-47 tag.
      const htmlLang = language === "pt" ? "pt-BR" : language;
      document.documentElement.setAttribute("lang", htmlLang);
    } catch (err) {}
    return this._ts.use(language);
  }

  getLanguageBasedOnURL(url: string) {
    const urlParts = url.split("/");
    let lang: string = urlParts[urlParts.length - 1];
    const indexOfQueryParams = lang.indexOf("?");
    if (indexOfQueryParams != -1) {
      lang = lang.substr(0, indexOfQueryParams);
    }

    switch (lang.toLowerCase()) {
      case "pt-br":
        return "pt";
      case "en":
        return "en";
      case "es":
        return "es";
      default:
        return undefined;
        break;
    }
  }

  registrateLanguage(language: string) {
    switch (language) {
      case "pt":
        this.languageService.registerCulture("pt-BR");
        break;
      case "en":
        this.languageService.registerCulture("en-US");
        if (environment.platformId == "mx") {
          this.languageService.registerCulture("en-mx");
          this._ts.setDefaultLang("en-mx");
          this._ts.use("en-mx");
        }
        break;
      case "es":
        this.languageService.registerCulture("es-MX");
        break;
      default:
        this.languageService.registerCulture("pt-BR");
    }
  }

  removeLanguageFromURL(url: string) {
    let newUrl = url;
    const urlParams = url.split("/");
    if (urlParams.length >= 2) {
      let langParam: string = urlParams[urlParams.length - 1];
      const langPathURL: string = urlParams[urlParams.length - 2];
      if (this.isLanguageParam(langParam)) {
        newUrl = urlParams.slice(0, urlParams.length - 2).join("/");
      } else if (langPathURL === "lang") {
        newUrl = urlParams.slice(0, urlParams.length - 2).join("/");
      }
    }
    return newUrl;
  }

  isLanguageParam(requestParam: string) {
    return this.supportedLanguages.includes(requestParam);
  }

  private getUrlExtension(url) {
    return url
      .split(/[#?]/)[0]
      .split(".")
      .pop()
      .trim();
  }

  public addCurrentLanguageToPath(currentPath: string) {
    if (currentPath) {
      const ext = this.getUrlExtension(currentPath);
      if (ext !== currentPath) {
        return currentPath;
      }
    }

    const homeURI = this.separateURIAndQueryParams(currentPath);
    const url = homeURI.url;
    const queryParams = homeURI.queryParams;

    // Normalize the PATH before re-appending query params: on the home page,
    // when switching to the deployment's DEFAULT (suffixless) language, the
    // cleaned + suffixed path collapses to "" — and `history.replaceState(_, "",
    // "")` is a no-op (an empty URL resolves to the CURRENT document URL), so a
    // stale `/lang/<code>` segment would otherwise survive. Forcing "/" resets the
    // address bar to the clean root. Doing it here (not in
    // updateUrlWithoutNavigation) keeps query params attached to a real path.
    let newURL = this.normalizeRootPath(
      this.concatLanguagePathURL(this.removeLanguageFromURL(url))
    );
    if (queryParams) {
      newURL = newURL.concat(queryParams);
    }
    // Address-bar rewrite is browser-only and guarded inside
    // updateUrlWithoutNavigation (SSR-safe).
    this.updateUrlWithoutNavigation(newURL);
    return newURL;
  }

  public updateUrlWithoutNavigation(newUrl: string) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const state = history.state;
    history.replaceState(state, "", newUrl);
  }

  /**
   * Rewrite the address bar to `path` while PRESERVING the current query string
   * (tracking/utm params). Browser-only (SSR no-op — reads `window`). Used by
   * solution/product detail pages to re-slug their URL to the active language
   * after data resolves, without dropping query params the way a bare
   * `updateUrlWithoutNavigation(path)` would.
   */
  public replacePathKeepingQuery(path: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.updateUrlWithoutNavigation(path.concat(window.location.search || ""));
  }

  /**
   * Register the CURRENT route's `{ internalLang -> localized address-bar path }`
   * map. Called by `MetaTagService.addHreflangTags` with the URLs it already
   * builds per language, so the switcher can re-slug instantly (see
   * `applyLocalizedPathForLang`). Pure state — no `window`, SSR-safe.
   */
  public setLocalizedPathMap(map: { [lang: string]: string } | null): void {
    this.localizedPathMap = map;
  }

  /**
   * Drop the registered map. Called on every `NavigationEnd` so a route with no
   * translated slug does not inherit the previous page's map.
   */
  public clearLocalizedPathMap(): void {
    this.localizedPathMap = null;
  }

  /**
   * Instantly rewrite the address bar to the CURRENT page's URL for `lang`,
   * using the map registered by `addHreflangTags`. No-op (returns false) when no
   * map is registered or it has no entry for `lang` — the caller then relies on
   * the normal suffix behavior. `en-mx` is normalized to `en`. Browser-only.
   * Returns whether a localized path was applied.
   */
  public applyLocalizedPathForLang(lang: string): boolean {
    if (!isPlatformBrowser(this.platformId) || !this.localizedPathMap) {
      return false;
    }
    const normalized = lang === "en-mx" ? "en" : lang;
    const path = this.localizedPathMap[normalized];
    if (!path) {
      return false;
    }
    this.replacePathKeepingQuery(path);
    return true;
  }

  /**
   * Guarantee a non-empty address-bar path: the application root is written as
   * "/", never the empty string. On the home page, when the active language is
   * the deployment's DEFAULT (suffixless) language, the cleaned + suffixed path
   * is "" — and `history.replaceState(state, "", "")` does NOT change the URL (an
   * empty URL resolves to the current document URL), so a stale `/lang/<code>`
   * suffix would survive and `app.component` would reapply the old language on
   * reload. Returning "/" forces the clean root instead.
   *
   * Platform-agnostic — it fires for every deployment's native/default language
   * (BR = pt, MX = es, CO = es, LATAM = en) whenever the home URL still carries a
   * non-default `/lang/<code>` suffix at switch time. Deep paths are unaffected:
   * their cleaned path is already non-empty and is returned unchanged.
   */
  private normalizeRootPath(path: string): string {
    return path && path.trim().length ? path : "/";
  }

  private separateURIAndQueryParams(currentPath: string): HomePageURI {
    const indexQueryParam = currentPath.indexOf("?");
    let url = currentPath;
    let queryParams = null;

    if (indexQueryParam != -1) {
      url = currentPath.substring(0, indexQueryParam);
      queryParams = currentPath.substring(indexQueryParam, currentPath.length);
    }

    return { url: url, queryParams: queryParams };
  }

  /**
   * Thin delegate kept for backward compatibility. Drives the address-bar
   * normalization on every `NavigationEnd` (via `addCurrentLanguageToPath`). It
   * now emits the suffix conditionally — clean URL for the deployment default,
   * `/lang/<code>` otherwise — by delegating to `applyLangSuffix`.
   */
  concatLanguagePathURL(currentPath: string) {
    if (currentPath == null || currentPath === undefined) {
      return currentPath;
    }
    return this.applyLangSuffix(
      this.applyNewsSlugPrefix(currentPath, this.currentLanguage),
      this.currentLanguage,
      this.getDefaultLanguage()
    );
  }

  /**
   * The single canonical builder for INTERNAL (current-deployment) localized
   * links. Strips any pre-existing `/lang/<code>` segment, then appends the
   * suffix only for a non-default language. `lang` defaults to the active
   * language. The suffix decision uses the CURRENT platform's default.
   */
  buildLocalizedPath(basePath: string, lang?: string): string {
    if (basePath == null || basePath === undefined) {
      return basePath;
    }
    const language = lang || this.getCurrentLanguage();
    const homeURI = this.separateURIAndQueryParams(basePath);
    const cleanPath = this.removeLanguageFromURL(homeURI.url);
    let localized = this.applyLangSuffix(
      this.applyNewsSlugPrefix(cleanPath, language),
      language,
      this.getDefaultLanguage()
    );
    if (homeURI.queryParams) {
      localized = localized.concat(homeURI.queryParams);
    }
    return localized;
  }

  /**
   * Single source of truth mapping a `platformId` to its absolute base URL.
   * Every base URL is sourced from `environment.*` (env-aware: LOCAL/STG/PROD) —
   * NO domain is hardcoded here. `latam`/`co` now have dedicated env fields, so
   * the previously hand-rolled `https://siila.com` / `https://siila.com.co`
   * literals live nowhere in the code.
   */
  getPlatformBaseUrl(platformId: string = environment.platformId): string {
    switch (platformId) {
      case "br":
        return environment.applicationURLBr;
      case "mx":
        return environment.applicationURLMx;
      case "latam":
        return environment.applicationURLLatam;
      case "co":
        return environment.applicationURLCo;
      default:
        return environment.applicationURL;
    }
  }

  private joinBaseAndPath(base: string, path: string): string {
    if (!path) {
      return base;
    }
    const b = base.endsWith("/") ? base.slice(0, base.length - 1) : base;
    const p = path.startsWith("/") ? path : "/".concat(path);
    return b.concat(p);
  }

  /**
   * Build an absolute URL to ANOTHER SiiLA deployment (cross-country navigation:
   * press, resource, the `es` redirect, etc.). The base URL is resolved from
   * `environment.*` via `getPlatformBaseUrl(targetPlatform)`, and the `/lang`
   * suffix is decided against the **target** platform's default language. When
   * `lang` is omitted it defaults to the target's default — i.e. a clean URL.
   */
  buildCrossCountryUrl(
    targetPlatform: string,
    path: string = "",
    lang?: string
  ): string {
    const base = this.getPlatformBaseUrl(targetPlatform);
    const targetDefault = this.getDefaultLanguage(targetPlatform);
    const language = lang || targetDefault;
    const fullUrl = this.joinBaseAndPath(base, path);
    return this.applyLangSuffix(fullUrl, language, targetDefault);
  }

  /**
   * Build an absolute URL into the MX `fibra-analytics` deep links. Always targets
   * the MX deployment; `lang` defaults to the active language, and the suffix is
   * applied against MX's default (`es`) — clean for `es`, `/lang/en` for `en`.
   */
  buildFibraAnalyticsUrl(
    path: string = "/fibra-analytics",
    lang?: string
  ): string {
    return this.buildCrossCountryUrl(
      "mx",
      path,
      lang || this.getCurrentLanguage()
    );
  }

  /**
   * Single policy point for analytics (Matomo/GA) tracking URLs. The user-facing
   * URL policy now omits the default-language `/lang` suffix, but analytics is
   * kept on its OWN policy here so historical dashboards are not fragmented:
   * this currently returns the tracking URL unchanged. Change ONLY this method to
   * alter the analytics URL shape deliberately (e.g. strip `/lang` for clean
   * aggregation) — every tracking call site routes through here.
   */
  buildTrackingUrl(url: string): string {
    return url;
  }
}
